import { useState } from 'react';
import {
  Banner,
  Button,
  DiffView,
  Markdown,
  Modal,
  MonoLabel,
  Skeleton,
  Tag,
  TextAreaField,
  TextField,
  Toast
} from '../components/primitives';
import {
  isCustomized,
  TASK_STATUS_LABELS,
  TASK_STATUS_SOFT_WARN,
  type TaskStatusInstruction,
  type TaskStatusInstructionVersion,
  type TaskStatusKey
} from '../lib/models/taskStatuses';
import {
  useSettingsTaskStatusesVM,
  type OverrideRole
} from '../lib/vm/useSettingsTaskStatusesVM';
import { SettingsBackLink } from '../components/settings/SettingsBackLink';
import { formatRelativeTime } from '../lib/relativeTime';
import './SettingsTaskStatuses.css';

const READ_ONLY_NOTE =
  'You can read the per-status instructions so you understand what your agent is told. Owners and Admins author them.';
const MANAGE_NOTE =
  'Edit a status’ description and default instruction inline, and author per-role overrides. The preview at the top of each row shows the live merged Layer 1+2+3 context an agent sees. Save persists the row; History opens the version overlay where you can diff and restore.';
const PREVIEW_NOTE =
  'Read-only — the merged agent context for a task at this status, as your function role would see it. Re-renders as you edit.';
const SOFT_WARN_NOTE =
  'Approaching 4000 chars — consider trimming. Long instructions eat agent context window.';

function Chevron({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className="set-ts-chevron"
      data-expanded={expanded ? 'true' : 'false'}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 4l4 4-4 4" />
    </svg>
  );
}

/**
 * `/settings/task-statuses` (turn 11.2f — History overlay + diff + restore).
 *
 * Six fixed status rows, each collapsed to a summary and expandable to an
 * inline editor: the live merged Layer 1+2+3 preview, a plain-text Description
 * input, a markdown Default-instruction editor with a soft-warn char counter,
 * per-role override sub-rows, and a Save / Cancel / History actions row. Every
 * org member can view; Members get read-only rendered text. `History ▸` opens a
 * right-docked overlay listing the status' versions (default + each override
 * interleaved) with a side-by-side diff and — for Owner/Admin — Restore.
 */
export function SettingsTaskStatuses() {
  const vm = useSettingsTaskStatusesVM();

  return (
    <section
      className="set-ts-page"
      data-testid="settings-task-statuses"
      data-status={vm.status}
      data-viewer-role={vm.viewerRole ?? ''}
    >
      <SettingsBackLink />
      {vm.status === 'loading' ? (
        <div className="set-ts-loading" data-testid="settings-ts-loading">
          <Skeleton height={48} />
          <Skeleton height={56} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </div>
      ) : null}

      {vm.status === 'error' ? (
        <Banner kind="error" data-testid="settings-ts-error">
          {vm.error ?? "Couldn't load task statuses. Try again."}
        </Banner>
      ) : null}

      {vm.status === 'unauth' ? (
        <Banner kind="default" data-testid="settings-ts-unauth">
          You need to sign in to view task statuses.
        </Banner>
      ) : null}

      {vm.status === 'empty' ? (
        <Banner kind="default" data-testid="settings-ts-no-org">
          You don't belong to any org yet.
        </Banner>
      ) : null}

      {vm.status === 'ready' ? <ReadyView vm={vm} /> : null}

      {vm.historyStatus ? (
        <HistoryOverlay vm={vm} status={vm.historyStatus} />
      ) : null}

      {vm.saveToast ? (
        <Toast onDismiss={vm.actions.clearSaveToast}>{vm.saveToast}</Toast>
      ) : null}
      {vm.restoreToast ? (
        <Toast onDismiss={vm.actions.clearRestoreToast}>
          {vm.restoreToast}
        </Toast>
      ) : null}
    </section>
  );
}

function ReadyView({
  vm
}: {
  vm: ReturnType<typeof useSettingsTaskStatusesVM>;
}) {
  return (
    <>
      <header className="set-ts-head">
        <h1 className="set-ts-title">Task statuses</h1>
        <p className="set-ts-lede">
          Per-status agent instructions. These get appended as Layer 3 of the
          agent context for any task at that status.
        </p>
      </header>

      <div className="set-ts-rows" data-testid="settings-ts-rows">
        {vm.rows.map((row) => (
          <StatusRow key={row.status} vm={vm} row={row} />
        ))}
      </div>

      <Banner
        kind="default"
        data-testid={
          vm.canManage ? 'settings-ts-manage-note' : 'settings-ts-member-note'
        }
      >
        {vm.canManage ? MANAGE_NOTE : READ_ONLY_NOTE}
      </Banner>
    </>
  );
}

function StatusRow({
  vm,
  row
}: {
  vm: ReturnType<typeof useSettingsTaskStatusesVM>;
  row: TaskStatusInstruction;
}) {
  const status = row.status;
  const expanded = vm.expanded[status];
  const value = vm.editorValue(status);
  const customized =
    isCustomized({
      status,
      description: value.description,
      instructionMd: value.instructionMd
    }) || vm.hasAnyOverride(status);

  return (
    <div
      className="set-ts-row"
      data-expanded={expanded ? 'true' : 'false'}
      data-testid={`settings-ts-row-${status}`}
    >
      <button
        type="button"
        className="set-ts-summary"
        aria-expanded={expanded}
        onClick={() => vm.actions.toggleExpand(status)}
        data-testid={`settings-ts-summary-${status}`}
      >
        <Chevron expanded={expanded} />
        <span className="set-ts-name">{TASK_STATUS_LABELS[status]}</span>
        <span className="set-ts-desc-preview">{value.description}</span>
        {customized ? (
          <Tag data-testid={`settings-ts-customized-${status}`}>customized</Tag>
        ) : null}
      </button>

      {expanded ? (
        <div
          className="set-ts-body"
          data-testid={`settings-ts-body-${status}`}
        >
          <RowEditor vm={vm} row={row} />
        </div>
      ) : null}
    </div>
  );
}

function RowEditor({
  vm,
  row
}: {
  vm: ReturnType<typeof useSettingsTaskStatusesVM>;
  row: TaskStatusInstruction;
}) {
  const status = row.status;
  const value = vm.editorValue(status);

  // Members get read-only rendered text — no inputs, no controls.
  if (!vm.canManage) {
    return (
      <>
        <MergedPreview vm={vm} row={row} />
        <div className="set-ts-field">
          <MonoLabel as="div" size="sm" tracking="wide">
            Description
          </MonoLabel>
          <p
            className="set-ts-readonly-desc"
            data-testid={`settings-ts-readonly-desc-${status}`}
          >
            {value.description || 'No description.'}
          </p>
        </div>
        <div className="set-ts-field">
          <MonoLabel as="div" size="sm" tracking="wide">
            Default instruction
          </MonoLabel>
          <Markdown
            className="set-ts-readonly-instr"
            dataTestId={`settings-ts-readonly-instr-${status}`}
          >
            {value.instructionMd || 'No instruction set.'}
          </Markdown>
        </div>
        <RoleOverrides vm={vm} row={row} />
        <div
          className="set-ts-actions"
          data-testid={`settings-ts-actions-${status}`}
        >
          <Button
            variant="ghost"
            className="set-ts-history-btn"
            onClick={() => vm.actions.openHistory(status)}
            data-testid={`settings-ts-history-${status}`}
          >
            History ▸
          </Button>
        </div>
      </>
    );
  }

  const charCount = value.instructionMd.length;
  const overWarn = charCount > TASK_STATUS_SOFT_WARN;
  const dirty = vm.isStatusDirty(status);
  const saving = vm.saving[status] ?? false;
  const saveError = vm.saveError[status];

  return (
    <>
      <MergedPreview vm={vm} row={row} />
      <TextField
        id={`settings-ts-desc-${status}`}
        label="Description"
        className="set-ts-field"
        value={value.description}
        onChange={(e) => vm.actions.editDescription(status, e.target.value)}
        placeholder="One-line gloss for this status…"
        data-testid={`settings-ts-desc-${status}`}
      />

      <TextAreaField
        id={`settings-ts-instr-${status}`}
        label="Default instruction (markdown)"
        className="set-ts-field set-ts-instr-field"
        value={value.instructionMd}
        onChange={(e) =>
          vm.actions.editDefaultInstruction(status, e.target.value)
        }
        rows={8}
        placeholder="Write the per-status agent instruction…"
        data-testid={`settings-ts-instr-${status}`}
      />
      <MonoLabel
        as="div"
        size="sm"
        tone={overWarn ? 'fg' : 'muted'}
        className="set-ts-char-counter"
        data-testid={`settings-ts-char-counter-${status}`}
        data-warn={overWarn ? 'true' : 'false'}
      >
        {`${charCount} chars${overWarn ? ` · ${SOFT_WARN_NOTE}` : ''}`}
      </MonoLabel>

      <RoleOverrides vm={vm} row={row} />

      {saveError ? (
        <Banner
          kind="error"
          data-testid={`settings-ts-save-error-${status}`}
        >
          {saveError}
        </Banner>
      ) : null}

      <div className="set-ts-actions" data-testid={`settings-ts-actions-${status}`}>
        <Button
          variant="primary"
          onClick={() => void vm.actions.saveStatus(status)}
          disabled={!dirty || saving}
          data-testid={`settings-ts-save-${status}`}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => vm.actions.cancelEdit(status)}
          disabled={!dirty || saving}
          data-testid={`settings-ts-cancel-${status}`}
        >
          Cancel
        </Button>
        <Button
          variant="ghost"
          className="set-ts-history-btn"
          onClick={() => vm.actions.openHistory(status)}
          data-testid={`settings-ts-history-${status}`}
        >
          History ▸
        </Button>
      </div>
    </>
  );
}

/**
 * The live merged Layer 1+2+3 preview at the top of an expanded status row —
 * read-only, three labeled sections, exactly the agent context a task at this
 * status would receive for the viewer's function role. Re-renders as the
 * editors below it change (the VM derives Layer 3 from the working copies).
 */
function MergedPreview({
  vm,
  row
}: {
  vm: ReturnType<typeof useSettingsTaskStatusesVM>;
  row: TaskStatusInstruction;
}) {
  const status = row.status;
  const preview = vm.mergedPreview(status);
  const layer3Label =
    preview.layer3Source === 'override'
      ? `Per-status for ${TASK_STATUS_LABELS[status]} (Layer 3 — ${preview.layer2Label} override)`
      : `Per-status for ${TASK_STATUS_LABELS[status]} (Layer 3)`;

  return (
    <div
      className="set-ts-preview"
      data-testid={`settings-ts-preview-${status}`}
    >
      <MonoLabel as="div" size="sm" tracking="wide">
        Live merged preview
      </MonoLabel>
      <p className="set-ts-preview-note">{PREVIEW_NOTE}</p>

      <div className="set-ts-preview-layer">
        <MonoLabel as="div" size="sm" tone="muted">
          Org standards (Layer 1)
        </MonoLabel>
        <Markdown
          className="set-ts-preview-md"
          dataTestId={`settings-ts-preview-l1-${status}`}
        >
          {preview.layer1 || '_No org standards set._'}
        </Markdown>
      </div>

      <div className="set-ts-preview-layer">
        <MonoLabel as="div" size="sm" tone="muted">
          {`Role rule for ${preview.layer2Label} (Layer 2)`}
        </MonoLabel>
        <Markdown
          className="set-ts-preview-md"
          dataTestId={`settings-ts-preview-l2-${status}`}
        >
          {preview.layer2 || '_No role rule for this role._'}
        </Markdown>
      </div>

      <div className="set-ts-preview-layer">
        <MonoLabel as="div" size="sm" tone="muted">
          {layer3Label}
        </MonoLabel>
        <Markdown
          className="set-ts-preview-md"
          dataTestId={`settings-ts-preview-l3-${status}`}
        >
          {preview.layer3 || '_No instruction set for this status._'}
        </Markdown>
      </div>
    </div>
  );
}

/**
 * The Role-overrides group inside an expanded status row — one collapsible
 * sub-row per function role, each marked `using default` or `CUSTOM`.
 * Expanding a sub-row reveals a per-role instruction editor (Owner/Admin) or
 * the role's rendered override text (Member).
 */
function RoleOverrides({
  vm,
  row
}: {
  vm: ReturnType<typeof useSettingsTaskStatusesVM>;
  row: TaskStatusInstruction;
}) {
  if (vm.roles.length === 0) return null;
  const status = row.status;
  return (
    <div
      className="set-ts-overrides"
      data-testid={`settings-ts-overrides-${status}`}
    >
      <MonoLabel as="div" size="sm" tracking="wide">
        Role overrides
      </MonoLabel>
      <div className="set-ts-override-rows">
        {vm.roles.map((role) => (
          <RoleOverrideRow
            key={role.id}
            vm={vm}
            status={status}
            role={role}
          />
        ))}
      </div>
    </div>
  );
}

const HISTORY_EMPTY_NOTE =
  'No prior versions yet. Save this status — or one of its role overrides — to create the first snapshot.';
const RESTORE_CONFIRM_NOTE =
  'The current content for this cell is replaced with this version. A snapshot of the current state is appended to history first, so the restore is reversible.';

/**
 * The right-docked History overlay for a status — the default row's snapshots
 * interleaved with every role override's, newest-first. Selecting a version
 * reveals a side-by-side diff against the cell's current content; Owner/Admin
 * get a `Restore this version` confirm. Members can browse + diff but not
 * restore.
 */
function HistoryOverlay({
  vm,
  status
}: {
  vm: ReturnType<typeof useSettingsTaskStatusesVM>;
  status: TaskStatusKey;
}) {
  const versions = vm.versionsForStatus(status);
  const [selectedId, setSelectedId] = useState<string | null>(
    versions[0]?.id ?? null
  );
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const selected =
    versions.find((v) => v.id === selectedId) ?? versions[0] ?? null;

  const roleLabel = (v: TaskStatusInstructionVersion): string =>
    v.roleId === null
      ? 'default'
      : vm.roles.find((r) => r.id === v.roleId)?.label ?? 'role override';

  const authorHandle = (userId: string | null): string =>
    (userId && vm.handles[userId]) || 'someone';

  const restoredFromLabel = (
    v: TaskStatusInstructionVersion
  ): string | null => {
    if (!v.restoredFromVersionId) return null;
    const src = versions.find((x) => x.id === v.restoredFromVersionId);
    if (!src) return null;
    return `(restored from ${formatRelativeTime(src.createdAt)})`;
  };

  return (
    <Modal
      open
      placement="right"
      onClose={vm.actions.closeHistory}
      title={`History — ${TASK_STATUS_LABELS[status]}`}
      dataTestId="settings-ts-history-overlay"
      footer={
        <Button
          variant="ghost"
          onClick={vm.actions.closeHistory}
          data-testid="settings-ts-history-close"
        >
          Close
        </Button>
      }
    >
      {versions.length === 0 ? (
        <p
          className="set-ts-history-empty"
          data-testid="settings-ts-history-empty"
        >
          {HISTORY_EMPTY_NOTE}
        </p>
      ) : (
        <>
          <ul className="set-ts-history-list" data-testid="settings-ts-history-list">
            {versions.map((v) => {
              const restored = restoredFromLabel(v);
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    className="set-ts-history-item"
                    aria-pressed={v.id === selected?.id}
                    data-selected={v.id === selected?.id ? 'true' : 'false'}
                    onClick={() => setSelectedId(v.id)}
                    data-testid={`settings-ts-history-item-${v.id}`}
                  >
                    <span className="set-ts-history-item-head">
                      <span className="set-ts-history-author">
                        {authorHandle(v.createdBy)}
                      </span>
                      <MonoLabel as="span" size="sm" tone="muted">
                        {formatRelativeTime(v.createdAt)}
                      </MonoLabel>
                    </span>
                    <span className="set-ts-history-item-meta">
                      <Tag accent={v.roleId !== null}>{roleLabel(v)}</Tag>
                      {restored ? (
                        <MonoLabel as="span" size="sm" tone="muted">
                          {restored}
                        </MonoLabel>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {selected ? (
            <div
              className="set-ts-history-detail"
              data-testid="settings-ts-history-detail"
            >
              <DiffView
                before={selected.instructionMd}
                after={vm.currentInstructionMd(selected)}
                beforeLabel={`Version · ${formatRelativeTime(
                  selected.createdAt
                )}`}
                afterLabel="Current"
                dataTestId="settings-ts-history-diff"
              />

              {vm.restoreError ? (
                <Banner
                  kind="error"
                  data-testid="settings-ts-restore-error"
                >
                  {vm.restoreError}
                </Banner>
              ) : null}

              {vm.canManage ? (
                confirmId === selected.id ? (
                  <div
                    className="set-ts-history-confirm"
                    data-testid="settings-ts-restore-confirm-box"
                  >
                    <p className="set-ts-history-confirm-note">
                      {RESTORE_CONFIRM_NOTE}
                    </p>
                    <div className="set-ts-history-confirm-actions">
                      <Button
                        variant="ghost"
                        onClick={() => setConfirmId(null)}
                        data-testid="settings-ts-restore-cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        disabled={vm.restoringVersionId !== null}
                        onClick={() => {
                          void vm.actions.restoreVersion(selected.id);
                          setConfirmId(null);
                        }}
                        data-testid="settings-ts-restore-do"
                      >
                        {vm.restoringVersionId === selected.id
                          ? 'Restoring…'
                          : 'Restore'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    className="set-ts-history-restore-btn"
                    disabled={vm.restoringVersionId !== null}
                    onClick={() => setConfirmId(selected.id)}
                    data-testid="settings-ts-restore-open"
                  >
                    Restore this version
                  </Button>
                )
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Modal>
  );
}

function RoleOverrideRow({
  vm,
  status,
  role
}: {
  vm: ReturnType<typeof useSettingsTaskStatusesVM>;
  status: TaskStatusInstruction['status'];
  role: OverrideRole;
}) {
  const expanded = vm.roleExpanded[status]?.[role.id] ?? false;
  const custom = vm.isOverrideCustom(status, role.id);
  const value = vm.overrideEditorValue(status, role.id);
  const dirty = vm.isOverrideDirty(status, role.id);

  return (
    <div
      className="set-ts-role-row"
      data-expanded={expanded ? 'true' : 'false'}
      data-testid={`settings-ts-role-row-${status}-${role.key}`}
    >
      <button
        type="button"
        className="set-ts-role-summary"
        aria-expanded={expanded}
        onClick={() => vm.actions.toggleRoleExpand(status, role.id)}
        data-testid={`settings-ts-role-summary-${status}-${role.key}`}
      >
        <Chevron expanded={expanded} />
        <span className="set-ts-role-name">{role.label}</span>
        <Tag
          accent={custom}
          data-testid={`settings-ts-role-state-${status}-${role.key}`}
        >
          {custom ? 'CUSTOM' : 'using default'}
        </Tag>
      </button>

      {expanded ? (
        <div
          className="set-ts-role-body"
          data-testid={`settings-ts-role-body-${status}-${role.key}`}
        >
          {vm.canManage ? (
            <>
              <TextAreaField
                id={`settings-ts-override-instr-${status}-${role.key}`}
                label={`${role.label} override (markdown)`}
                className="set-ts-field set-ts-instr-field"
                value={value}
                onChange={(e) =>
                  vm.actions.editOverrideInstruction(
                    status,
                    role.id,
                    e.target.value
                  )
                }
                rows={6}
                placeholder="Leave blank to use the status default…"
                data-testid={`settings-ts-override-instr-${status}-${role.key}`}
              />
              <div className="set-ts-override-hint">
                <MonoLabel as="div" size="sm">
                  {value.trim()
                    ? 'This role uses the text above instead of the status default.'
                    : 'Empty — this role uses the status default. Type to create an override.'}
                </MonoLabel>
                <Button
                  variant="ghost"
                  onClick={() =>
                    vm.actions.cancelOverrideEdit(status, role.id)
                  }
                  disabled={!dirty}
                  data-testid={`settings-ts-override-cancel-${status}-${role.key}`}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <Markdown
              className="set-ts-readonly-instr"
              dataTestId={`settings-ts-override-readonly-${status}-${role.key}`}
            >
              {value || 'Using the default instruction for this status.'}
            </Markdown>
          )}
        </div>
      ) : null}
    </div>
  );
}
